/* eslint-disable react/display-name */
import 'mapbox-gl/dist/mapbox-gl.css';

import { Transition } from '@headlessui/react';
import mapboxgl from 'mapbox-gl';
import type { MutableRefObject } from 'react';
import React, { useMemo, useState } from 'react';
import type { MapRef } from 'react-map-gl';
import Map, { FullscreenControl, Marker, Popup } from 'react-map-gl';

import type { MarkerType } from '@/types/map.model';
import type SpecificLocationProps from '@/types/SpecificLocation.type';
import { getLocationDisplay } from '@/utils/helper';
import { getBounds } from '@/utils/mapUtils';

import CustomPin from './pin';

type VentureBoxProps = {
  markers: MarkerType[];
  handleHideVentures?: (
    event?: React.SyntheticEvent | KeyboardEvent | TouchEvent | MouseEvent
  ) => void;
  mapRef: MutableRefObject<MapRef | undefined>;
  locations: SpecificLocationProps[];
};

const VenturesMapBox = ({ markers, mapRef, locations }: VentureBoxProps) => {
  const [selectedMarkerId, setSelectedMarkerId] = useState('');
  const markerBounds = getBounds(markers);

  const renderMarkers = useMemo(
    () =>
      markers.map((marker) => {
        return (
          <Marker
            key={marker.id}
            longitude={marker.longitude}
            latitude={marker.latitude}
            onClick={() => setSelectedMarkerId(marker.id)}
          >
            <CustomPin />
          </Marker>
        );
      }),
    [markers]
  );

  if (markers.length <= 0) return <div>Loading...</div>;

  return (
    <div className="relative h-full">
      <Map
        // @ts-ignore:next-line
        ref={mapRef}
        initialViewState={{
          zoom: 12,
          latitude: markers[0]?.latitude,
          longitude: markers[0]?.longitude,
          bounds: markers.length > 1 ? markerBounds : undefined,
          fitBoundsOptions: {
            // padding map container to make marker look good
            padding: 60,
            // animation duration when bounds
            duration: 800,
            // offset: [0, 0],
          },
        }}
        mapStyle="mapbox://styles/mapbox/dark-v9"
        mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
        attributionControl={false}
        scrollZoom={false}
        onLoad={() => {
          mapRef.current?.addControl(
            new mapboxgl.NavigationControl(),
            'bottom-right'
          );
        }}
      >
        {renderMarkers}
        {locations.map((item) => {
          return (
            <Transition
              appear
              key={item.id}
              show={selectedMarkerId === item.id}
              enter="transition duration-200 ease-in-out"
              enterFrom="transform scale-50 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-in-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-50 opacity-0"
              className="origin-top"
            >
              <Popup
                focusAfterOpen
                anchor="bottom"
                // @ts-ignore:next-line
                longitude={Number(item.longitude)}
                // @ts-ignore:next-line
                latitude={Number(item.latitude)}
                onClose={() => {
                  setSelectedMarkerId('');
                }}
                style={{
                  padding: 0,
                }}
                className="p-0"
                offset={[0, -20]}
                closeOnClick
                closeButton={false}
              >
                <div className="rounded-lg bg-white px-2">
                  <h4 className="capitalize">
                    {getLocationDisplay(item.country, item.city)}
                  </h4>
                </div>
              </Popup>
            </Transition>
          );
        })}
        <FullscreenControl />
      </Map>
    </div>
  );
};

export default VenturesMapBox;
